import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Upload, User, ChevronLeft, ChevronRight, Check, X as XIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { adminServices } from "../services/AdminServices";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const paginationStyles = {
  wrapper: {
    display: "flex", justifyContent: "center", alignItems: "center",
    width: "100%", padding: "16px 0", gap: "8px", flexWrap: "wrap"
  },
  link: {
    color: "#6b7280", fontSize: "0.875rem", cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "none", border: "none", padding: "6px 8px"
  },
  linkDisabled: { color: "#9ca3af", cursor: "not-allowed", opacity: 0.7 },
  pageNum: {
    color: "#6b7280", fontSize: "0.875rem", cursor: "pointer",
    minWidth: "32px", height: "32px", display: "inline-flex",
    alignItems: "center", justifyContent: "center",
    padding: "0 8px", border: "none", backgroundColor: "transparent", borderRadius: "6px"
  },
  pageNumActive: { backgroundColor: "#e5e7eb", color: "#374151", fontWeight: 500 },
  ellipsis: { color: "#6b7280", fontSize: "0.875rem", padding: "0 4px", minWidth: "24px", textAlign: "center" }
};

function ActivityPagination({ total, rowsPerPage, currentPage, onChangePage }) {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) { pages.push("..."); pages.push(totalPages); }
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1); pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("..."); pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div style={paginationStyles.wrapper}>
      <button type="button" aria-label="Previous page"
        style={{ ...paginationStyles.link, ...(!canPrev ? paginationStyles.linkDisabled : {}) }}
        onClick={() => canPrev && onChangePage(currentPage - 1)} disabled={!canPrev}
      >
        <ChevronLeft size={18} /> Previous
      </button>
      {getPageNumbers().map((p, i) =>
        p === "..." ? (
          <span key={`e-${i}`} style={paginationStyles.ellipsis}>...</span>
        ) : (
          <button key={p} type="button"
            style={{ ...paginationStyles.pageNum, ...(p === currentPage ? paginationStyles.pageNumActive : {}) }}
            onClick={() => onChangePage(p)}
          >{p}</button>
        )
      )}
      <button type="button" aria-label="Next page"
        style={{ ...paginationStyles.link, ...(!canNext ? paginationStyles.linkDisabled : {}) }}
        onClick={() => canNext && onChangePage(currentPage + 1)} disabled={!canNext}
      >
        Next <ChevronRight size={18} />
      </button>
    </div>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-PH", { month: "2-digit", day: "2-digit", year: "2-digit" }).replace(/\//g, "/");
  } catch {
    return dateStr;
  }
};

const BucorOrderDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiData = location.state?.orderData;
  const orderData = apiData || {
    requestNo: "OS-26-01001",
    regDate: "",
    requestType: "Special Order Request",
    requestedByName: "",
    facility: "",
    status: "Pending",
    remarks: "",
    merchantName: "",
    pdlNumber: "",
    storeCode: "",
    location: ""
  };

  // Map API response: requestNo, regDate, requestedByName, facility, status, remarks, items
  const requestId = apiData?.requestNo ?? orderData.requestId ?? orderData.requestNo ?? "—";
  const issuedDate = formatDate(apiData?.regDate ?? orderData.regDate ?? orderData.date);
  const merchantName = apiData?.requestedByName ?? orderData.merchantName ?? orderData.requestedByName ?? "—";
  const pdlNumber = apiData?.pdlNumber ?? orderData.pdlNumber ?? "—";
  const storeCode = apiData?.storeCode ?? orderData.storeCode ?? "—";
  const locationVal = apiData?.facility ?? apiData?.location ?? orderData.location ?? orderData.facility ?? "—";
  const requestType = apiData?.requestType ?? orderData.requestType ?? "Special Order";
  const status = apiData?.status ?? orderData.status ?? "Pending";
  const isPending = (status ?? "").toUpperCase() === "PENDING";
  const isStatusFinal = ["APPROVED", "REJECTED"].includes((status ?? "").toUpperCase());
  const hasOrderId = apiData?.orderId != null;

  const [items] = useState(() => {
    const apiItems = apiData?.items ?? [];
    if (apiItems.length > 0) {
      return apiItems.map((it) => ({
        id: it.id ?? it.productId,
        productId: it.productId ?? it.product?.id ?? it.id,
        sku: it.sku ?? it.product?.sku ?? "—",
        barcode: it.barcode ?? it.product?.barcode ?? "—",
        description: it.productName ?? it.description ?? it.product?.name ?? "—",
        quantity: it.quantity ?? 1,
        unitPrice: it.unitPrice ?? it.price ?? 0,
        total: (it.quantity ?? 1) * (it.unitPrice ?? it.price ?? 0),
        remarks: it.remarks
      }));
    }
    return [
      { id: 1, productId: 1, sku: "—", barcode: "—", description: "—", quantity: 0, unitPrice: 0, total: 0 },
    ];
  });

  const [reasonForListing] = useState(apiData?.remarks ?? "");
  const [bucorRemarks, setBucorRemarks] = useState("");

  // No-order-id selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemTypes, setItemTypes] = useState({});
  const [maxOrderQtys, setMaxOrderQtys] = useState({});
  const [itemApprovalStatus, setItemApprovalStatus] = useState({});

  const handleCheckboxChange = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i.id));
    }
  };
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const ACTIVITY_PAGE_SIZE = 3;
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, status: null });
  const [successModal, setSuccessModal] = useState({ open: false, status: null });
  const [itemsApproved, setItemsApproved] = useState(false);

  const bucorApprovalId = apiData?.id ?? apiData?.bucorApprovalId;
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const openConfirm = (newStatus) => setConfirmModal({ open: true, status: newStatus });
  const closeConfirm = () => setConfirmModal({ open: false, status: null });

  const handleUpdateStatus = async () => {
    const newStatus = confirmModal.status;
    if (!bucorApprovalId || !newStatus) return;
    closeConfirm();
    try {
      setStatusUpdating(true);
      const resp = await adminServices.updateBucorApprovalStatus(bucorApprovalId, newStatus, bucorRemarks || "");
      if (resp?.status === 200 || resp?.status === 204) {
        setSuccessModal({ open: true, status: newStatus });
        if (!hasOrderId && newStatus === "APPROVED") {
          setItemsApproved(true);
        }
      }
      const res = await adminServices.getBucorApprovalHistory(bucorApprovalId);
      setActivityLog(Array.isArray(res?.data) ? res.data : []);
      setActivityPage(1);
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const buildItemsPayload = (approvalMap) => {
    return items.map((item) => {
      const approvalStatus = approvalMap[item.id];
      const itemType = itemTypes[item.id] ?? "";
      const maxQty = maxOrderQtys[item.id];
      const itemLimit = maxQty != null && maxQty !== "" ? parseInt(String(maxQty), 10) : 0;
      return {
        id: Number(item.id) || 0,
        productId: Number(item.productId ?? item.id) || 0,
        remarks: item.remarks ?? "",
        status: approvalStatus === "approved" ? "Approved" : "Rejected",
        itemType: itemType.toUpperCase() || "",
        itemLimit: isNaN(itemLimit) ? 0 : itemLimit,
        qty: Number(item.quantity) || 0
      };
    });
  };

  const handleApproveSelected = async () => {
    if (selectedItems.length === 0) return;
    if (!hasOrderId) {
      const selectedSet = new Set(selectedItems);
      const approvalMap = {};
      items.forEach((item) => {
        approvalMap[item.id] = selectedSet.has(item.id) ? "approved" : "rejected";
      });
      try {
        setStatusUpdating(true);
        const itemsPayload = buildItemsPayload(approvalMap);
        await adminServices.updateBucorApprovalItems(itemsPayload);
        setItemApprovalStatus(approvalMap);
        setSelectedItems([]);
        setItemsApproved(true);
        const res = await adminServices.getBucorApprovalHistory(bucorApprovalId);
        if (bucorApprovalId) setActivityLog(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to update items:", err);
      } finally {
        setStatusUpdating(false);
      }
      return;
    }
    openConfirm("APPROVED");
  };

  const handleRejectSelected = async () => {
    if (selectedItems.length === 0) return;
    if (!hasOrderId) {
      const selectedSet = new Set(selectedItems);
      const approvalMap = {};
      items.forEach((item) => {
        approvalMap[item.id] = selectedSet.has(item.id) ? "rejected" : "approved";
      });
      try {
        setStatusUpdating(true);
        const itemsPayload = buildItemsPayload(approvalMap);
        await adminServices.updateBucorApprovalItems(itemsPayload);
        setItemApprovalStatus(approvalMap);
        setSelectedItems([]);
        setItemsApproved(true);
        const res = await adminServices.getBucorApprovalHistory(bucorApprovalId);
        if (bucorApprovalId) setActivityLog(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to update items:", err);
      } finally {
        setStatusUpdating(false);
      }
      return;
    }
    openConfirm("REJECTED");
  };

  const handleCompleteRequest = () => {
    openConfirm("APPROVED");
  };

  const handleExportPdf = async () => {
    const el = printRef.current;
    if (!el) return;
    try {
      setExporting(true);
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let y = 0;
      while (y < imgHeight) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, -y, imgWidth, imgHeight);
        y += pageHeight;
      }
      pdf.save(`${requestId || "order"}.pdf`);
    } catch (err) {
      console.error("Failed to export PDF:", err);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!bucorApprovalId) return;
    setActivityLoading(true);
    adminServices
      .getBucorApprovalHistory(bucorApprovalId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setActivityLog(list);
      })
      .catch((err) => console.error("Failed to load activity log:", err))
      .finally(() => setActivityLoading(false));
  }, [bucorApprovalId]);

  const getStatusBadgeClass = (s) => {
    const val = (s ?? "").toUpperCase();
    if (val === "PENDING") return "bucor-detail-status-pending";
    if (val === "APPROVED") return "bucor-detail-status-approved";
    if (val === "REJECTED") return "bucor-detail-status-rejected";
    return "";
  };

  const showSelectAndActions = !hasOrderId && !itemsApproved && !isStatusFinal;

  return (
    <div className="dashboard-content">
      <div className="bucor-order-detail-page" ref={printRef}>
        {/* Top Navigation */}
        <div className="bucor-detail-top">
          <button 
            className="bucor-back-btn"
            onClick={() => navigate("/bucor-module")}
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <button className="bucor-export-pdf-btn" onClick={handleExportPdf} disabled={exporting}>
            <Upload size={18} />
            {exporting ? "Exporting..." : "Export PDF"}
          </button>
        </div>

        {/* Order Number and Status */}
        <div className="bucor-detail-header">
          <h2 className="bucor-order-number">{requestId}</h2>
          <span className={`bucor-detail-status-badge ${getStatusBadgeClass(status)}`}>
            {status}
          </span>
        </div>

        {/* Order Information Section */}
        {hasOrderId && (
          <div className="bucor-detail-section">
            <h3 className="bucor-detail-section-title">Order Information</h3>
            <div className="bucor-detail-info-grid">
              <div className="bucor-detail-info-item">
                <label>Order Type</label>
                <input type="text" value={requestType} readOnly className="bucor-detail-input" />
              </div>
              <div className="bucor-detail-info-item">
                <label>Issued Date</label>
                <input type="text" value={issuedDate} readOnly className="bucor-detail-input" />
              </div>
              <div className="bucor-detail-info-item">
                <label>Merchant Name</label>
                <input type="text" value={merchantName} readOnly className="bucor-detail-input" />
              </div>
              <div className="bucor-detail-info-item">
                <label>PDL#</label>
                <input type="text" value={pdlNumber} readOnly className="bucor-detail-input" />
              </div>
              <div className="bucor-detail-info-item">
                <label>Store Code</label>
                <input type="text" value={storeCode} readOnly className="bucor-detail-input" />
              </div>
              <div className="bucor-detail-info-item">
                <label>Location</label>
                <input type="text" value={locationVal} readOnly className="bucor-detail-input" />
              </div>
            </div>
          </div>
        )}

        {/* Item Details Section */}
        {hasOrderId ? (
          <div className="bucor-detail-section">
            <h3 className="bucor-detail-section-title">Item Details</h3>
            <div className="bucor-detail-table-container">
              <table className="bucor-detail-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Barcode</th>
                    <th>Item Description</th>
                    <th>QTY</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.sku}</td>
                      <td>{item.barcode}</td>
                      <td>{item.description}</td>
                      <td>
                        <input
                          type="number"
                          value={item.quantity}
                          readOnly
                          className="bucor-quantity-input"
                          style={{ cursor: "not-allowed", backgroundColor: "#f3f4f6", border: "none", outline: "none", borderRadius: 4, padding: "4px 8px", width: 60, textAlign: "center" }}
                        />
                      </td>
                      <td>₱{item.unitPrice.toLocaleString()}</td>
                      <td>₱{item.total.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bucor-item-approval-detail-section">
            <div className="bucor-item-approval-detail-section-header">
              <h2 className="bucor-item-approval-detail-section-title">Item Details</h2>
              {showSelectAndActions && (
                <div className="bucor-item-approval-detail-actions-top">
                  <span className="bucor-item-approval-selected-count">
                    {selectedItems.length} selected
                  </span>
                  <button
                    className="bucor-item-approval-approve-btn"
                    onClick={handleApproveSelected}
                    disabled={selectedItems.length === 0 || statusUpdating || isStatusFinal}
                  >
                    <Check size={18} />
                    Approve Selected
                  </button>
                  <button
                    className="bucor-item-approval-reject-btn"
                    onClick={handleRejectSelected}
                    disabled={selectedItems.length === 0 || statusUpdating || isStatusFinal}
                  >
                    <XIcon size={18} />
                    Reject Selected
                  </button>
                </div>
              )}
            </div>
            <div className="bucor-item-approval-detail-table-container">
              <table className="bucor-item-approval-detail-table">
                <thead>
                  <tr>
                    {showSelectAndActions && (
                      <th style={{ width: "50px" }}>
                        <input
                          type="checkbox"
                          checked={selectedItems.length === items.length && items.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    <th>Item Description</th>
                    <th style={{ width: "160px" }}>Item Type</th>
                    <th style={{ width: "130px" }}>Max. Order QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const type = itemTypes[item.id] ?? "";
                    const isLocked = type === "Regular" || type === "Special";
                    const status = itemApprovalStatus[item.id];
                    const rowBg = status === "rejected"
                      ? "#fee2e2"
                      : status === "approved"
                        ? (type === "Regular" ? "#f0fdf4" :
                           type === "Regulated" ? "#dcfce7" :
                           type === "Special" ? "#fff1f2" :
                           "#f0fdf4")
                        : "transparent";
                    return (
                      <tr key={item.id} style={{ backgroundColor: rowBg }}>
                        {showSelectAndActions && (
                          <td style={{ width: "50px" }}>
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(item.id)}
                              onChange={() => handleCheckboxChange(item.id)}
                            />
                          </td>
                        )}
                        <td style={{ fontWeight: 500 }}>{item.description}</td>
                        <td>
                          <select
                            className="bucor-item-approval-item-type-select"
                            value={type}
                            disabled={itemsApproved || isStatusFinal}
                            onChange={(e) => {
                              const val = e.target.value;
                              setItemTypes(prev => ({ ...prev, [item.id]: val }));
                              if (val === "Regular" || val === "Special") {
                                setMaxOrderQtys(prev => ({ ...prev, [item.id]: "" }));
                              }
                            }}
                            style={(itemsApproved || isStatusFinal) ? { cursor: "not-allowed", opacity: 0.8 } : {}}
                          >
                            <option value="">Select</option>
                            <option value="Regular">Regular</option>
                            <option value="Regulated">Regulated</option>
                            <option value="Special">Special</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="bucor-quantity-input"
                            value={isLocked ? "" : (maxOrderQtys[item.id] ?? "")}
                            onChange={(e) => !isLocked && !itemsApproved && !isStatusFinal && setMaxOrderQtys(prev => ({ ...prev, [item.id]: e.target.value }))}
                            placeholder="-"
                            readOnly={isLocked || itemsApproved || isStatusFinal}
                            style={{
                              width: "80px",
                              textAlign: "center",
                              borderRadius: 6,
                              padding: "5px 8px",
                              border: (isLocked || itemsApproved || isStatusFinal) ? "none" : "1px solid #d1d5db",
                              backgroundColor: (isLocked || itemsApproved || isStatusFinal) ? "#f3f4f6" : "#ffffff",
                              cursor: (isLocked || itemsApproved || isStatusFinal) ? "not-allowed" : "text",
                              outline: "none",
                              fontFamily: "Figtree, sans-serif",
                              fontSize: "0.875rem"
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reason for Listing */}
        <div className="bucor-detail-section">
          <label className="bucor-detail-label">Reason for listing</label>
          <div className="bucor-detail-text-display">
            {reasonForListing}
          </div>
        </div>

        {/* Bucor Remarks */}
        <div className="bucor-detail-section">
          <label className="bucor-detail-label">Bucor Remarks</label>
          <textarea
            className="bucor-detail-textarea"
            rows={3}
            value={bucorRemarks}
            onChange={(e) => !isStatusFinal && setBucorRemarks(e.target.value)}
            placeholder="Enter remarks..."
            readOnly={isStatusFinal}
            style={isStatusFinal ? { cursor: "not-allowed", backgroundColor: "#f9fafb" } : {}}
          />
        </div>

        {/* Action Buttons */}
        {!hasOrderId ? (
          <div className="bucor-item-approval-detail-actions">
            <button
              className="bucor-item-approval-complete-btn"
              onClick={handleCompleteRequest}
              disabled={statusUpdating || isStatusFinal}
            >
              {statusUpdating ? "Processing..." : "Complete Request"}
            </button>
          </div>
        ) : isPending && (
          <div className="bucor-detail-actions">
            <button
              className="bucor-reject-btn"
              onClick={() => openConfirm("REJECTED")}
              disabled={statusUpdating}
            >
              {statusUpdating ? "Updating..." : "Reject request"}
            </button>
            <button
              className="bucor-approve-btn"
              onClick={() => openConfirm("APPROVED")}
              disabled={statusUpdating}
            >
              {statusUpdating ? "Updating..." : "Approve request"}
            </button>
            <button className="bucor-save-btn" disabled={statusUpdating}>Save Changes</button>
          </div>
        )}

        {/* Activity Log */}
        {!hasOrderId ? (
          <div className="bucor-item-approval-activity-log-section">
            <h2 className="bucor-item-approval-activity-log-title">Activity Log</h2>
            <div className="bucor-item-approval-activity-log-list">
              {activityLoading ? (
                <div style={{ padding: "16px", color: "#6b7280", fontSize: "0.875rem" }}>Loading activity log...</div>
              ) : activityLog.length === 0 ? (
                <div style={{ padding: "16px", color: "#9ca3af", fontSize: "0.875rem" }}>No activity yet.</div>
              ) : (
                activityLog
                  .slice((activityPage - 1) * ACTIVITY_PAGE_SIZE, activityPage * ACTIVITY_PAGE_SIZE)
                  .map((entry, idx) => (
                    <div key={idx} className="bucor-item-approval-activity-log-item">
                      <div className="bucor-item-approval-activity-log-icon">
                        <User size={20} />
                      </div>
                      <div className="bucor-item-approval-activity-log-content">
                        <div className="bucor-item-approval-activity-log-user">{entry.userFullName ?? "—"}</div>
                        <div className="bucor-item-approval-activity-log-action">{entry.description ?? entry.action ?? "—"}</div>
                      </div>
                      <div className="bucor-item-approval-activity-log-timestamp">
                        {entry.date ? new Date(entry.date).toLocaleString("en-PH") : "—"}
                      </div>
                    </div>
                  ))
              )}
              {activityLog.length > ACTIVITY_PAGE_SIZE && (
                <ActivityPagination
                  total={activityLog.length}
                  rowsPerPage={ACTIVITY_PAGE_SIZE}
                  currentPage={activityPage}
                  onChangePage={setActivityPage}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="bucor-detail-section">
            <h3 className="bucor-detail-section-title">Activity Log</h3>
            <div className="bucor-activity-log">
              {activityLoading ? (
                <div style={{ padding: "16px", color: "#6b7280", fontSize: "0.875rem" }}>Loading activity log...</div>
              ) : activityLog.length === 0 ? (
                <div style={{ padding: "16px", color: "#9ca3af", fontSize: "0.875rem" }}>No activity yet.</div>
              ) : (
                <>
                  {activityLog
                    .slice((activityPage - 1) * ACTIVITY_PAGE_SIZE, activityPage * ACTIVITY_PAGE_SIZE)
                    .map((entry, idx) => (
                      <div key={idx} className="bucor-activity-item">
                        <div className="bucor-activity-left">
                          <div className="bucor-activity-icon">
                            <User size={20} />
                          </div>
                          <div>
                            <div className="bucor-activity-name">{entry.userFullName ?? "—"}</div>
                            <div className="bucor-activity-action">{entry.description ?? entry.action ?? "—"}</div>
                          </div>
                        </div>
                        <div className="bucor-activity-time">
                          {entry.date ? new Date(entry.date).toLocaleString("en-PH") : "—"}
                        </div>
                      </div>
                    ))}
                  {activityLog.length > ACTIVITY_PAGE_SIZE && (
                    <ActivityPagination
                      total={activityLog.length}
                      rowsPerPage={ACTIVITY_PAGE_SIZE}
                      currentPage={activityPage}
                      onChangePage={setActivityPage}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.open && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.4)"
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px", padding: "32px 28px",
            width: "340px", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
          }}>
            <h3 style={{ margin: "0 0 10px", fontSize: "1.1rem", fontWeight: 700, color: "#111" }}>
              {confirmModal.status === "APPROVED" ? "Approve Request" : "Reject Request"}
            </h3>
            <p style={{ margin: "0 0 28px", fontSize: "0.9rem", color: "#6b7280", lineHeight: 1.5 }}>
              Are you sure you want to {confirmModal.status === "APPROVED" ? "approve" : "reject"} this request?
              This action cannot be undone.
            </p>
            <button
              onClick={handleUpdateStatus}
              disabled={statusUpdating}
              style={{
                width: "100%", padding: "12px", borderRadius: "10px",
                backgroundColor: confirmModal.status === "APPROVED" ? "#1e293b" : "#dc2626",
                color: "#fff", fontWeight: 600, fontSize: "0.95rem",
                border: "none", cursor: "pointer", marginBottom: "10px"
              }}
            >
              {statusUpdating ? "Processing..." : confirmModal.status === "APPROVED" ? "Confirm and Approve" : "Confirm and Reject"}
            </button>
            <button
              onClick={closeConfirm}
              disabled={statusUpdating}
              style={{
                width: "100%", padding: "12px", borderRadius: "10px",
                backgroundColor: "#fff", color: "#374151", fontWeight: 600,
                fontSize: "0.95rem", border: "1.5px solid #e5e7eb", cursor: "pointer"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal.open && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.4)"
        }}>
          <div style={{
            background: "#fff", borderRadius: "16px", padding: "32px 28px",
            width: "340px", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
          }}>
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%",
              backgroundColor: successModal.status === "APPROVED" ? "#dcfce7" : "#fee2e2",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px"
            }}>
              <span style={{ fontSize: "1.6rem" }}>
                {successModal.status === "APPROVED" ? "✓" : "✕"}
              </span>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "1.1rem", fontWeight: 700, color: "#111" }}>
              Successfully {successModal.status === "APPROVED" ? "Approved" : "Rejected"}
            </h3>
            <p style={{ margin: "0 0 24px", fontSize: "0.875rem", color: "#6b7280" }}>
              The request has been {successModal.status === "APPROVED" ? "approved" : "rejected"} successfully.
            </p>
            <button
              onClick={() => { setSuccessModal({ open: false, status: null }); navigate("/bucor-module"); }}
              style={{
                width: "100%", padding: "12px", borderRadius: "10px",
                backgroundColor: "#1e293b", color: "#fff", fontWeight: 600,
                fontSize: "0.95rem", border: "none", cursor: "pointer"
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BucorOrderDetail;

